<template>
  <v-dialog
    :value="true"
    max-width="1024px"
    scrollable
    persistent
  >
    <v-card
      id="ab-bucket-details"
      :loading="loading"
    >
      <v-card-title>
        <span>Ab Bucket: {{ bucketData.bucket_name }} </span>

        <v-spacer />

        <v-btn
          icon
          @click="handleClose"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <div
        v-if="loading"
        class="d-flex flex-column align-center py-4 px-4"
      >
        <h3 class="mb-6 mt-2">
          Loading Bucket Details...
        </h3>
        <v-progress-circular
          :size="50"
          color="primary"
          indeterminate
        />
      </div>
      <template v-else>
        <v-card-text>
          <v-container justify="space-around">
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model.trim="bucketData.bucket_name"
                  required
                  label="Bucket Name"
                  :error-messages="bucketNameErrors"
                  @blur="$v.bucketData.bucket_name.$touch()"
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-select
                  v-model="bucketData.bucket_type"
                  :items="bucketTypes"
                  label="Bucket Type"
                  disabled
                  :error-messages="bucketTypeErrors"
                  @blur="$v.bucketData.bucket_type.$touch()"
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-slider
                  v-model.lazy.number="bucketData.default_ratio"
                  label="Default Router %"
                  style="margin-top: 15px"
                  class="align-center"
                  :min="0"
                  :step="0.01"
                  :max="1"
                  :error-messages="championSplitErrors"
                  @blur="$v.bucketData.default_ratio.$touch()"
                >
                  <template #append>
                    <div style="padding-right: 10px; font-weight: bold">
                      {{ bucketData.default_ratio | percentage }}
                    </div>
                  </template>
                </v-slider>
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  :value="formatPercentage(bucketData.testing_ratio)"
                  label="Testing %"
                  readonly
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-checkbox
                  v-model="bucketData.is_active"
                  label="Is Active"
                  inset
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-textarea
                  v-model.trim="bucketData.description"
                  label="Description"
                  auto-grow
                  rows="3"
                  :error-messages="descriptionErrors"
                  @blur="$v.bucketData.description.$touch()"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider />

        <v-card-actions class="py-4">
          <v-spacer />

          <v-btn
            class="btn-bg-deep-blue"
            text-color="white"
            @click="handleSave"
          >
            Save changes
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';
import { formatPercentage } from '@/filters';
import { required } from 'vuelidate/lib/validators';

export default {
  name: 'AbBucketForm',

  props: {
    bucketId: {
      type: Number,
      required: true,
    },
  },

  emits: ['bucket-saved', 'close'],

  data() {
    return {
      bucketData: {
        id: null,
        bucket_name: '',
        bucket_type: '',
        testing_ratio: '',
        default_ratio: '',
        description: '',
        is_active: false,
        created_at: '',
        updated_at: '',
      },
      bucketTypes: ['Walkup', 'Return', 'Purchased', 'Prescreen'],
      loading: false,

      lendingStrategies: [],
    };
  },

  computed: {
    bucketNameErrors() {
      const errors = [];
      if (!this.$v.bucketData.bucket_name.$dirty) {
        return errors;
      }
      if (!this.$v.bucketData.bucket_name.required) {
        errors.push('Bucket name is required.');
      }
      return errors;
    },

    bucketTypeErrors() {
      const errors = [];
      if (!this.$v.bucketData.bucket_type.$dirty) {
        return errors;
      }
      if (!this.$v.bucketData.bucket_type.required) {
        errors.push('Bucket type is required.');
      }
      return errors;
    },

    descriptionErrors() {
      const errors = [];
      if (!this.$v.bucketData.description.$dirty) {
        return errors;
      }
      if (!this.$v.bucketData.description.required) {
        errors.push('Description is required.');
      }
      return errors;
    },

    championSplitErrors() {
      const errors = [];
      if (!this.$v.bucketData.default_ratio.$dirty) {
        return errors;
      }
      if (!this.$v.bucketData.default_ratio.required) {
        errors.push('Default Router % is required.');
      }
      return errors;
    },
  },

  watch: {
    bucketId: {
      async handler(newVal) {
        await this.fetchAbBucket(newVal);
      },
      immediate: true,
    },

    'bucketData.default_ratio': {
      handler(newVal) {
        this.bucketData.testing_ratio = 1 - newVal;
      },
    },
  },

  methods: {
    formatPercentage,
    ...mapActions('notification', ['setNotification']),

    handleSave() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.setNotification({
          color: 'red',
          message: 'Please fix the errors in the form.',
        });
        return;
      }

      this.saveAbBucket(this.bucketData);
    },

    async saveAbBucket(payload) {
      try {
        const { data } = await this.$axios.put(`api/v1/ab-buckets/${this.bucketData.id}`, payload);

        this.setNotification({
          color: 'green',
          message: 'Bucket saved successfully',
        });

        this.$emit('bucket-saved', data);
      } catch (error) {
        this.setNotification({
          color: 'red',
          message: 'Error saving bucket',
        });
      }
    },

    async fetchAbBucket(id) {
      try {
        this.loading = true;

        const { data } = await this.$axios.get(`api/v1/ab-buckets/${id}`, {
          params: {
            withLendingStrategies: true,
          },
        });

        this.bucketData = {
          ...this.bucketData,
          ...data?.data,
        };
        this.lendingStrategies = data?.lendingStrategies || [];

        console.log('Fetched bucket data:', data);
      } catch (error) {
        this.setNotification({
          color: 'red',
          message: 'Error fetching bucket details',
        });
      } finally {
        this.loading = false;
      }
    },

    handleClose() {
      this.$emit('close');
    },
  },

  validations: {
    bucketData: {
      bucket_name: { required },
      description: { required },
      bucket_type: { required },
      is_active: { required },
      default_ratio: { required },
      testing_ratio: { required },
    },
  },
};
</script>

<style>
#ab-bucket-details .v-toolbar__title,
#ab-bucket-details .v-btn__content {
  text-transform: none !important;
}

#ab-bucket-details .v-toolbar__content {
  padding-left: 0;
  padding-right: 0;
}
</style>
